import { ChangeDetectionStrategy, Component, Injector, OnInit, ViewEncapsulation } from '@angular/core';
import { FiFormTodoService } from '@fi-app/fi-form/fi-form-todo.service';
import { takeUntil } from 'rxjs/operators';
import { IForm } from '@fi-shared/fi-form-shared/fi-form-shared';
import { FiDateFormatOptions } from '@fi-shared/utils';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { AppTextService } from '@fi-shared/app-text';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { MatSortDirection, ObjectTypes } from '@fi-shared/types';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';

@Component({
    selector: 'fi-system-notifications-forms',
    templateUrl: 'forms.component.html',
    styleUrls: ['./forms.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-system-notifications-forms' },
    standalone: false,
})
export class SystemNotificationsFormsComponent extends FiInfiniteScrollDataSource<IForm> implements OnInit {
    private readonly EndDateColumnName: string = 'EndDate';
    private readonly NameColumnName: string = 'Name';

    protected skipReloadOnSortChanged = true;
    protected defaultSortColumnName: string = this.EndDateColumnName;
    protected FiStateNames = FiStateNames;

    public itemsListType = FiItemsListType.SystemNotificationsForms;
    public objectType = ObjectTypes.Form;

    constructor(
        private formTodoService: FiFormTodoService,
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
        private fiNotificationHelperService: FiNotificationHelperService,
        injector: Injector,
    ) {
        super(injector, {});
    }

    protected initSelectedSort(): void {
        super.initSelectedSort();

        const haveItemsWithEndDate: boolean = this.formTodoService.todoForms.some((item) => !!item.EndDate);

        if (haveItemsWithEndDate) {
            this.selectedSort.active = this.EndDateColumnName;
        } else {
            this.selectedSort.active = this.NameColumnName;
        }
        this.selectedSort.direction = MatSortDirection.Ascending;
    }

    public ngOnInit(): void {
        this.subscribeToTodoForms();
    }

    private subscribeToTodoForms(): void {
        this.formTodoService.todoForms$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((todoForms: IForm[]) => {
            this.reloadData();
        });
    }

    protected isFormNotVisited(form: IForm): boolean {
        return this.fiNotificationHelperService.isNotVisited(form);
    }

    protected getFormEndData(form: IForm): string {
        const currentForm = this.formTodoService.todoForms.find((f) => f.Id === form.Id);
        if (currentForm.EndDate) {
            return this.fiFormatDateService.formatDate(new Date(currentForm.EndDate), FiDateFormatOptions);
        }
        return this.appTextService.get('form.continuous_sharing');
    }

    public getListData(): Promise<IForm[]> {
        return Promise.resolve([
            ...this.formTodoService.todoForms.map((form) => ({
                ...form,
                EndDate: form.EndDate ? form.EndDate : Number.MAX_SAFE_INTEGER.toString(),
            })),
        ]);
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
